import { PropsWithChildren, useContext } from 'react';

import { styled } from '@mui/material';

import { CAST_AI } from '@cast/constants';
import {
  HexIcon,
  Icons,
  Paper,
  ExternalLink,
  Placeholder as _StatusMessage,
} from '@cast/design-system';

import { WorkloadDetailsContext } from '../../../_context';
import { useHpaDisabledReasons } from '../../_hooks/useHpaDisabledReasons';

import { getDocsLink } from '@cast/utils';

// TODO (Justas): Should be possible to style through css
const iconSize = 50;

const StatusMessage = styled(_StatusMessage)({
  maxWidth: 495,
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: 60,
  [`& .${_StatusMessage.classNames.iconWrapper}`]: {
    marginBottom: 4,
  },
  [`& .${_StatusMessage.classNames.body}`]: { marginTop: 0 },
  [`& .${_StatusMessage.classNames.actions}`]: { marginTop: 12 },
});

export const AlertsWrapper = ({ children }: PropsWithChildren) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const {
    typeNotSupported,
    isCastAgentOutdatedForHpa,
    isWoopAgentOutdatedForHpa,
  } = useHpaDisabledReasons(workload);

  if (typeNotSupported) {
    return (
      <Paper sx={{ py: 32 }}>
        <StatusMessage
          icon={
            <HexIcon
              variant="notification"
              size={iconSize}
              icon={<Icons.Lock />}
            />
          }
          title="Horizontal rightsizing is not available for this workload type"
          body={
            <>
              Please refer to our{' '}
              <ExternalLink link={getDocsLink('docs_woop_hpa')} noIcon>
                documentation
              </ExternalLink>{' '}
              to read about the workload requirements for enabling horizontal
              rightsizing.
            </>
          }
          sx={{ width: 343, mt: 0 }}
          testId="type-not-supported-warning"
        />
      </Paper>
    );
  }

  if (isWoopAgentOutdatedForHpa || isCastAgentOutdatedForHpa) {
    let title: string;
    let body: string;
    if (isWoopAgentOutdatedForHpa && isCastAgentOutdatedForHpa) {
      title = `${CAST_AI} agent and workload autoscaler out of date`;
      body = `The installed versions of the ${CAST_AI} agent and Workload Autoscaler component are out of date and does not support Kubernetes Horizontal Pod Autoscaling, rendering the autoscaling feature inoperable.`;
    } else if (isWoopAgentOutdatedForHpa) {
      title = `${CAST_AI} workload autoscaler out of date`;
      body =
        'The installed version of the Workload Autoscaler component is out of date and does not support Kubernetes Horizontal Pod Autoscaling, rendering the autoscaling feature inoperable.';
    } else {
      title = `${CAST_AI} agent out of date`;
      body = `The installed version of the ${CAST_AI} agent is out of date and does not support Kubernetes Horizontal Pod Autoscaling, rendering the autoscaling feature inoperable.`;
    }
    return (
      <Paper sx={{ py: 32 }}>
        <StatusMessage
          icon={
            <HexIcon
              variant="error"
              size={iconSize}
              icon={<Icons.WarningCircle />}
            />
          }
          title={title}
          body={body}
          sx={{ mt: 0 }}
          testId="hpa-outdated-warning"
        />
      </Paper>
    );
  }

  return <>{children}</>;
};
